import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setError('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const name = formData.name.trim();
    const email = formData.email.trim().toLowerCase();
    const subject = formData.subject.trim();
    const message = formData.message.trim();

    if (!name || name.length < 2) {
      setError('Please enter a valid name (at least 2 characters)');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!subject) {
      setError('Please select a subject');
      return;
    }

    if (!message || message.length < 10) {
      setError('Message must be at least 10 characters');
      return;
    }

    const linkCount = (message.match(/https?:\/\//g) || []).length;
    if (linkCount > 3) {
      setError('Maximum 3 links allowed in message');
      return;
    }

    setIsLoading(true);

    const encoded = new URLSearchParams({
      'form-name': 'contact',
      name,
      email,
      subject,
      message,
    }).toString();

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encoded,
    })
      .then((res) => {
        if (res.ok) {
          setIsSubmitted(true);
        } else {
          setError('Something went wrong. Please try again or email us directly.');
        }
      })
      .catch(() => {
        setError('Network error. Please try again or email us directly.');
      })
      .finally(() => setIsLoading(false));
  };

  if (isSubmitted) {
    return (
      <div className="form-page">
        <div className="form-container success-page">
          <h1>Message Sent</h1>
          <div className="success-card">
            <div className="success-badge">Received</div>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '2rem' }}>
              Thank you for reaching out. Our team will review your message and get back to you within 24 hours.
            </p>
            <p className="success-note">For urgent matters, email us directly at <strong>hello@somalicopyright.agency</strong></p>
            <div className="success-actions">
              <Link to="/" className="btn-primary">Return to Homepage <span className="btn-arrow">&rarr;</span></Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="form-container">
        <h1>Get in Touch</h1>
        <p>Have a question or need help with a copyright issue? Send us a message and our team will respond within 24 hours.</p>

        <div aria-live="polite">{error && <div className="form-error">{error}</div>}</div>

        <form onSubmit={handleSubmit} name="contact" method="POST">
          <input type="hidden" name="form-name" value="contact" />
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <label>Don't fill this out <input name="bot-field" tabIndex={-1} autoComplete="off" /></label>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Your Information</h3>
            <div className="form-group">
              <label htmlFor="contact-name">Full Name</label>
              <input id="contact-name" type="text" name="name" value={formData.name} onChange={handleChange} required maxLength={100} placeholder="Enter your full name" />
            </div>
            <div className="form-group">
              <label htmlFor="contact-email">Email Address</label>
              <input id="contact-email" type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" />
              <small className="form-hint">We'll reply to this address</small>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Your Message</h3>
            <div className="form-group">
              <label htmlFor="contact-subject">Subject</label>
              <select id="contact-subject" name="subject" value={formData.subject} onChange={handleChange} required>
                <option value="">Select a topic</option>
                <option value="DMCA Takedown Request">DMCA Takedown Request</option>
                <option value="Content Theft Report">Content Theft Report</option>
                <option value="Pricing Question">Pricing Question</option>
                <option value="Partnership Inquiry">Partnership Inquiry</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="contact-message">Message</label>
              <textarea id="contact-message" name="message" value={formData.message} onChange={handleChange} required maxLength={2000} placeholder="Tell us about your situation — include platform names and any relevant details." rows={6} />
              <small className="form-hint">{formData.message.length}/2000 characters</small>
            </div>
          </div>

          <button type="submit" className="btn-primary form-submit" disabled={isLoading} aria-live="polite">
            {isLoading ? 'Sending...' : <>Send Message <span className="btn-arrow">&rarr;</span></>}
          </button>
        </form>

        <div style={{ marginTop: '2.5rem', textAlign: 'center', padding: '1.5rem', background: 'var(--accent-light)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Prefer email?</p>
          <a href="mailto:hello@somalicopyright.agency" style={{ fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', fontSize: '1rem' }}>hello@somalicopyright.agency</a>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
